/**
 * User-programmable regex rules that map a formula pattern to a kind label.
 * Stored in localStorage; loaded by the scanner before classification.
 *
 * Rules are checked in array order; first match wins. Built-in classification
 * (sum.from* etc.) runs only when no user rule matches.
 *
 * Example rules:
 *   { pattern: "\\bCONCATENATE\\s*\\(", flags: "i", kind: "text" }
 *   { pattern: "\\bVLOOKUP\\s*\\(", flags: "i", kind: "lookup" }
 *   { pattern: "\\bIF\\s*\\(.*\\bIF\\s*\\(", flags: "i", kind: "if.nested" }
 */

/**
 * Region-shape restriction for a rule:
 *   - "h" → horizontal (1 row × N columns)
 *   - "v" → vertical   (N rows × 1 column)
 *   - "r" → rectangular (M rows × N columns, both > 1)
 *   - "s" → single cell (1 × 1)
 * Empty array = no restriction (rule matches any shape).
 */
export type FormulaShape = "h" | "v" | "r" | "s";
export const ALL_SHAPES: readonly FormulaShape[] = ["h", "v", "r", "s"] as const;

export interface FormulaKindRule {
  /** Stable id (uuid-ish, generated on add). */
  id: string;
  /** Human label shown in the editor and in tooltips. */
  name: string;
  /** Regex pattern (string form). */
  pattern: string;
  /** Regex flags (e.g. "i" for case-insensitive). */
  flags: string;
  /** Kind label to assign when the regex matches the formula. */
  kind: string;
  /**
   * Shape restriction: which region shapes are eligible. Empty = any shape.
   * Multiple shapes = OR (the rule matches if the region's shape is any of these).
   */
  shapes: FormulaShape[];
  enabled: boolean;
}

/** Compute the shape of a region from its bounds. */
export function regionShape(
  minRow: number,
  minCol: number,
  maxRow: number,
  maxCol: number
): FormulaShape {
  const h = maxRow - minRow + 1;
  const w = maxCol - minCol + 1;
  if (h === 1 && w === 1) return "s";
  if (w === 1) return "v";
  if (h === 1) return "h";
  return "r";
}

const STORAGE_KEY = "cogseven.toolkit.formulaKindRules";

// Sensible defaults — the user can disable, edit, or extend these.
const DEFAULT_RULES: FormulaKindRule[] = [
  {
    id: "default-concat",
    name: "CONCATENATE / CONCAT / TEXTJOIN → text",
    pattern: "\\b(CONCATENATE|CONCAT|TEXTJOIN)\\s*\\(",
    flags: "i",
    kind: "text.concat",
    shapes: [],
    enabled: true,
  },
  {
    id: "default-vlookup",
    name: "VLOOKUP / XLOOKUP / HLOOKUP → lookup",
    pattern: "\\b(VLOOKUP|HLOOKUP|XLOOKUP)\\s*\\(",
    flags: "i",
    kind: "lookup",
    shapes: [],
    enabled: true,
  },
  {
    id: "default-iferror",
    name: "IFERROR / IFNA → guarded",
    pattern: "\\b(IFERROR|IFNA)\\s*\\(",
    flags: "i",
    kind: "guarded",
    shapes: [],
    enabled: true,
  },
  {
    id: "default-date",
    name: "DATE / EDATE / EOMONTH / YEAR / MONTH / DAY → date",
    pattern: "\\b(DATE|EDATE|EOMONTH|YEAR|MONTH|DAY|TODAY|NOW|DATEDIF)\\s*\\(",
    flags: "i",
    kind: "date",
    shapes: [],
    enabled: true,
  },
];

export function loadKindRules(): FormulaKindRule[] {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [...DEFAULT_RULES];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [...DEFAULT_RULES];
    return parsed.map(sanitizeRule).filter((r): r is FormulaKindRule => r !== null);
  } catch {
    return [...DEFAULT_RULES];
  }
}

export function saveKindRules(rules: FormulaKindRule[]): void {
  const cleaned = rules.map(sanitizeRule).filter((r): r is FormulaKindRule => r !== null);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
}

export function resetKindRules(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function defaultKindRules(): FormulaKindRule[] {
  return DEFAULT_RULES.map((r) => ({ ...r }));
}

/**
 * Run user rules against a formula + region shape; return the first matching
 * kind, or null if nothing matched. Malformed regex patterns are skipped
 * silently. A rule with a non-empty `shapes` array only fires when `shape`
 * is one of the listed shapes; an empty (or missing) `shapes` array means
 * no restriction.
 */
export function matchKindRules(
  formula: string,
  shape: FormulaShape,
  rules: FormulaKindRule[]
): string | null {
  for (const rule of rules) {
    if (!rule.enabled) continue;
    if (!rule.pattern) continue;
    // Shape restriction
    if (rule.shapes && rule.shapes.length > 0 && !rule.shapes.includes(shape)) {
      continue;
    }
    let re: RegExp;
    try {
      re = new RegExp(rule.pattern, rule.flags || "");
    } catch {
      continue;
    }
    if (re.test(formula)) return rule.kind;
  }
  return null;
}

function sanitizeRule(raw: unknown): FormulaKindRule | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const id = typeof obj.id === "string" && obj.id ? obj.id : generateId();
  const name = typeof obj.name === "string" ? obj.name : "";
  const pattern = typeof obj.pattern === "string" ? obj.pattern : "";
  const flags = typeof obj.flags === "string" ? obj.flags : "";
  const kind = typeof obj.kind === "string" ? obj.kind : "";
  const enabled = typeof obj.enabled === "boolean" ? obj.enabled : true;
  // shapes: optional in older serialized rules. Filter to known values; dedupe.
  let shapes: FormulaShape[] = [];
  if (Array.isArray(obj.shapes)) {
    const set = new Set<FormulaShape>();
    for (const s of obj.shapes) {
      if (s === "h" || s === "v" || s === "r" || s === "s") set.add(s);
    }
    shapes = Array.from(set);
  }
  if (!kind) return null;
  return { id, name, pattern, flags, kind, shapes, enabled };
}

function generateId(): string {
  return "r-" + Math.random().toString(36).slice(2, 10);
}
